/**
 * Author-feedback helpers for session reports (slots, ratings, VSC validation).
 */

import type { SessionReport, SessionReportAuthorFeedback } from '@/types/inperson/report.types';
import type {
  SessionParticipant,
  SessionType,
  SessionWithParticipants,
} from '@/types/inperson/session.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';

/** Flat API rating (avoids the `@deprecated` on {@link SessionReport.rating}). */
type LegacyReportRating = { rating?: number | null };

/** Report slice used to normalize author feedbacks and display ratings. */
type ReportLike = Pick<SessionReport, 'authorFeedbacks' | 'authorId' | 'comment' | 'feeling'> &
  LegacyReportRating;

/** Legacy flat authorId / rating fields used when completing a slot. */
type ReportLegacyAuthorFields = Pick<SessionReport, 'authorId'> & LegacyReportRating;

/** In-progress author slot (rating / comment / feeling) before save. */
export type ReportFeedbackDraft = {
  rating: number | null;
  comment: string;
  feeling?: unknown;
};

/** One tutor/volunteer block on a multi-author in-person report. */
export interface TutorReportSection {
  authorUserId: string;
  role: 'tutor' | 'volunteer';
  displayName: string;
  feedback: SessionReportAuthorFeedback;
  tutorParticipant?: SessionParticipant;
  studentParticipants: SessionParticipant[];
}

/** Session slice needed to decide VSC validation gates. */
export type SessionForReport = Pick<SessionWithParticipants, 'type' | 'participants'>;

/** Options for {@link areAllTutorFeedbacksComplete} when checking whether all tutor slots are complete. */
export type ReportValidationOptions = {
  session?: SessionForReport;
  /** Exclude the connected VSC (volunteer) slot from the tutor count. */
  viewerUserId?: string;
};

/** Report fields needed to decide VSC validation gates. */
type ReportForValidation = Pick<
  SessionReport,
  | 'status'
  | 'authorFeedbacks'
  | 'authorId'
  | 'comment'
  | 'feeling'
  | 'subjectIds'
  | 'subject'
  | 'session'
> &
  LegacyReportRating;

/** Validation report that includes an id (bulk selection). */
type ReportWithId = ReportForValidation & { id: string };

/** Privileged report editors: VSC accounts use `volunteer` or `vsc`. */
const PRIVILEGED_REPORT_EDITOR_ROLES = [
  'ADMIN',
  'SUPER_ADMIN',
  'MANAGER',
  'COORDINATOR',
  'VSC',
  'VOLUNTEER',
] as const;

/** True for admin / coordinator / VSC roles that can edit every author slot. */
export function isPrivilegedReportEditor(roleCodes: string[] | undefined): boolean {
  if (!roleCodes?.length) return false;
  const upper = new Set(roleCodes.map((c) => (c ?? '').toUpperCase()));
  return PRIVILEGED_REPORT_EDITOR_ROLES.some((r) => upper.has(r));
}

/** Lowercased session type from the report or the loaded session. */
function resolveSessionType(
  report: Pick<SessionReport, 'session'>,
  session?: SessionForReport,
): string | null | undefined {
  const raw = report.session?.type ?? session?.type ?? null;
  if (raw == null) return null;
  return String(raw).toLowerCase().trim();
}

/** Pick the display author slot: matching author with rating, else first rated, else first. */
function pickDisplayAuthorFeedbackFromEntries(
  entries: SessionReportAuthorFeedback[],
  authorId: string,
): SessionReportAuthorFeedback | null {
  if (!entries.length) return null;
  return (
    entries.find((e) => e.idAuteur === authorId && e.rating != null) ??
    entries.find((e) => e.idAuteur === authorId) ??
    entries.find((e) => e.rating != null) ??
    entries[0]
  );
}

/** Merge denormalized flat rating/comment into the matching author slots. */
function enrichAuthorFeedbacksFromLegacy(
  entries: SessionReportAuthorFeedback[],
  report: ReportLike,
): SessionReportAuthorFeedback[] {
  if (!entries.length) return entries;
  const legacyR = report.rating ?? null;
  const legacyC = report.comment?.trim();
  const legacyF = report.feeling ?? null;
  if (legacyR == null && !legacyC && legacyF == null) return entries;
  const authorId = report.authorId ?? '';
  const display = pickDisplayAuthorFeedbackFromEntries(entries, authorId);
  const targetIds = new Set(
    [authorId, display?.idAuteur].filter((id): id is string => Boolean(id)),
  );
  if (!targetIds.size) return entries;
  return entries.map((e) =>
    targetIds.has(e.idAuteur)
      ? {
          idAuteur: e.idAuteur,
          rating: e.rating ?? legacyR,
          comment: e.comment?.trim() ? e.comment : legacyC || null,
          feeling: e.feeling ?? legacyF,
        }
      : e,
  );
}

/** Fill a matching author slot's rating from the report's legacy flat rating. */
function resolveEntryForCompletion(
  entry: SessionReportAuthorFeedback,
  report: ReportLegacyAuthorFields,
): SessionReportAuthorFeedback {
  const authorId = report.authorId ?? '';
  if (!authorId || entry.idAuteur !== authorId) return entry;
  return {
    ...entry,
    rating: entry.rating ?? report.rating ?? null,
  };
}

/** Report-level subjects shared across tutors. */
export function hasReportSubjects(report: Pick<SessionReport, 'subjectIds'>): boolean {
  return Array.isArray(report.subjectIds) && report.subjectIds.length > 0;
}

/** Slot rating required (backend: truthy `entry.rating`, DTO 1–5). */
export function hasRequiredAuthorRating(rating: number | null | undefined): boolean {
  return rating != null && Number.isFinite(Number(rating)) && Number(rating) > 0;
}

/** Slot comment required (backend: `entry.comment?.trim()`). */
export function hasRequiredAuthorComment(comment: string | null | undefined): boolean {
  return !!comment?.trim();
}

/** True when a draft slot has a rating, comment, or feeling. */
function feedbackSlotHasData(feedback: ReportFeedbackDraft | undefined): boolean {
  if (!feedback) return false;
  return feedback.rating != null || !!feedback.comment.trim() || feedback.feeling != null;
}

/** True when a draft slot has a required rating, comment, and a subject. */
function feedbackSlotIsSubmissionComplete(
  feedback: ReportFeedbackDraft | undefined,
  hasSubject: boolean,
): boolean {
  if (!feedback || !hasSubject) return false;
  return hasRequiredAuthorRating(feedback.rating) && hasRequiredAuthorComment(feedback.comment);
}

/** Resolves which author slot drives status for a non-privileged editor. */
export function resolveActorAuthorUserId(params: {
  authorUserId: string | undefined;
  feedbackByAuthor: ReadonlyMap<string, ReportFeedbackDraft>;
  sectionAuthorUserId?: string;
}): string | undefined {
  const { authorUserId, feedbackByAuthor, sectionAuthorUserId } = params;
  if (sectionAuthorUserId && feedbackByAuthor.has(sectionAuthorUserId)) {
    return sectionAuthorUserId;
  }
  if (authorUserId && feedbackByAuthor.has(authorUserId)) {
    return authorUserId;
  }
  return sectionAuthorUserId ?? authorUserId ?? feedbackByAuthor.keys().next().value;
}

/**
 * Status after save (aligned with backend `isActorFeedbackComplete` + partial draft).
 * Prefer letting the backend derive status; kept for callers that need a client-side hint.
 */
export function resolveSessionReportStatusAfterSave(params: {
  currentStatus: SessionReport['status'] | undefined;
  authorUserId: string | undefined;
  feedbackByAuthor: ReadonlyMap<string, ReportFeedbackDraft>;
  subjectIds: string[];
  canEditAllAuthors: boolean;
  sectionAuthorUserId?: string;
}): SessionReport['status'] | undefined {
  if (params.currentStatus === 'validated') return undefined;

  const hasSubject = params.subjectIds.length > 0;

  const hasAnyData = hasSubject || [...params.feedbackByAuthor.values()].some(feedbackSlotHasData);

  let next: SessionReport['status'] | undefined;

  if (params.canEditAllAuthors) {
    const anyComplete = [...params.feedbackByAuthor.values()].some((f) =>
      feedbackSlotIsSubmissionComplete(f, hasSubject),
    );
    if (anyComplete) next = 'submitted';
    else if (hasAnyData) next = 'draft';
  } else {
    const actorId = resolveActorAuthorUserId({
      authorUserId: params.authorUserId,
      feedbackByAuthor: params.feedbackByAuthor,
      sectionAuthorUserId: params.sectionAuthorUserId,
    });
    const actor = actorId ? params.feedbackByAuthor.get(actorId) : undefined;
    if (feedbackSlotIsSubmissionComplete(actor, hasSubject)) next = 'submitted';
    else if (hasAnyData || feedbackSlotHasData(actor)) next = 'draft';
  }

  if (!next) return undefined;

  if (params.currentStatus === 'submitted' && next === 'draft') {
    return undefined;
  }

  return next;
}

/** Normalize feedbacks from API `authorFeedbacks` or legacy flat fields. */
export function normalizeAuthorFeedbacks(report: ReportLike): SessionReportAuthorFeedback[] {
  let entries: SessionReportAuthorFeedback[];
  if (report.authorFeedbacks?.length) {
    entries = report.authorFeedbacks;
  } else if (report.rating != null || report.comment?.trim() || report.feeling != null) {
    entries = [
      {
        idAuteur: report.authorId,
        rating: report.rating ?? null,
        comment: report.comment ?? null,
        feeling: report.feeling ?? null,
      },
    ];
  } else {
    return [];
  }
  return enrichAuthorFeedbacksFromLegacy(entries, report);
}

/** Display entry (primary author / backend export `getDisplayAuthorFeedback`). */
export function getDisplayAuthorFeedback(report: ReportLike): SessionReportAuthorFeedback | null {
  const arr = normalizeAuthorFeedbacks(report);
  return pickDisplayAuthorFeedbackFromEntries(arr, report.authorId ?? '');
}

/** Mean of filled ratings in `authorFeedbacks` (1 decimal). */
export function getReportMeanRating(report: ReportLike): number | null {
  const legacyFields: ReportLegacyAuthorFields = {
    authorId: report.authorId,
    rating: report.rating ?? null,
  };
  const ratings = normalizeAuthorFeedbacks(report)
    .map((entry) => resolveEntryForCompletion(entry, legacyFields).rating)
    .filter((r): r is number => hasRequiredAuthorRating(r))
    .map(Number);

  if (ratings.length === 0) return null;
  const mean = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  return Math.round(mean * 10) / 10;
}

/** Rating shown on lists / cards: mean of author ratings. */
export function getReportDisplayRating(report: ReportLike): number | null {
  return getReportMeanRating(report);
}

/** True for whole-class reports with more than one tutor/volunteer author. */
export function isMultiAuthorReport(
  session: Pick<SessionWithParticipants, 'type'> | undefined,
  report: ReportLike,
): boolean {
  const feedbacks = normalizeAuthorFeedbacks(report);
  if (session?.type === 'whole_class' && feedbacks.length > 1) return true;
  if (session?.type === 'whole_class') {
    const nonStudents =
      session && 'participants' in session
        ? ((session as SessionWithParticipants).participants?.filter((p) => p.role !== 'student') ??
          [])
        : [];
    return nonStudents.length > 1;
  }
  return false;
}

/** Display name for a session participant, or a fallback when the user is missing. */
function participantDisplayName(p: SessionParticipant | undefined): string {
  if (!p?.user) return 'Participant inconnu';
  const u = p.user;
  return (
    u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Participant inconnu'
  );
}

/** Build per-tutor sections (paired students + author slot) for the report form. */
export function buildTutorReportSections(
  session: SessionWithParticipants | undefined,
  report: ReportLike,
): TutorReportSection[] {
  let feedbacks = normalizeAuthorFeedbacks(report);
  const participants = session?.participants ?? [];

  if (!feedbacks.length && session?.type === 'whole_class') {
    const nonStudents = participants
      .filter((p) => p.role !== 'student')
      .sort((a, b) => {
        const order = { tutor: 0, volunteer: 1, student: 99 };
        const ra = order[a.role as keyof typeof order] ?? 2;
        const rb = order[b.role as keyof typeof order] ?? 2;
        if (ra !== rb) return ra - rb;
        return a.userId.localeCompare(b.userId);
      });
    feedbacks = nonStudents.map((p) => ({
      idAuteur: p.userId,
      rating: null,
      comment: null,
      feeling: null,
    }));
  }

  if (!feedbacks.length && report.authorId) {
    feedbacks = [
      {
        idAuteur: report.authorId,
        rating: report.rating ?? null,
        comment: report.comment ?? null,
        feeling: report.feeling ?? null,
      },
    ];
  }

  const pairs = session?.studentTutorPairs ?? [];

  const studentIdsByTutor = new Map<string, string[]>();
  for (const pair of pairs) {
    const list = studentIdsByTutor.get(pair.tutorUserId) ?? [];
    if (!list.includes(pair.studentUserId)) {
      list.push(pair.studentUserId);
      studentIdsByTutor.set(pair.tutorUserId, list);
    }
  }

  return feedbacks.map((feedback) => {
    const tutorParticipant = participants.find(
      (p) => p.userId === feedback.idAuteur && (p.role === 'tutor' || p.role === 'volunteer'),
    );
    const role: 'tutor' | 'volunteer' =
      tutorParticipant?.role === 'volunteer' ? 'volunteer' : 'tutor';
    const studentUserIds = studentIdsByTutor.get(feedback.idAuteur) ?? [];
    const studentParticipants = studentUserIds
      .map((uid) => participants.find((p) => p.userId === uid && p.role === 'student'))
      .filter((p): p is SessionParticipant => !!p);

    return {
      authorUserId: feedback.idAuteur,
      role,
      displayName: participantDisplayName(tutorParticipant),
      feedback,
      tutorParticipant,
      studentParticipants,
    };
  });
}

/** Session students not listed under a tutor pair (whole class). */
export function getUnmatchedStudentsForReport(
  session: SessionWithParticipants | undefined,
  sections: TutorReportSection[],
): SessionParticipant[] {
  const participants = session?.participants ?? [];
  const matchedStudentIds = new Set(
    sections.flatMap((s) => s.studentParticipants.map((p) => p.userId)),
  );

  return participants.filter((p) => p.role === 'student' && !matchedStudentIds.has(p.userId));
}

/** Section heading for unmatched students (singular vs plural count). */
export function getUnmatchedStudentsSectionTitle(count: number): string {
  if (count === 1) return getRoleLabel('student');
  return `${getRoleLabelPlural('student')} (${count})`;
}

/** @deprecated Use {@link getUnmatchedStudentsForReport}. */
export function getUnmatchedReportParticipants(
  session: SessionWithParticipants | undefined,
  sections: TutorReportSection[],
): SessionParticipant[] {
  return getUnmatchedStudentsForReport(session, sections);
}

/** Tutor slot treated as written (VSC follow-up): rating filled (1–5). */
export function isAuthorFeedbackSlotComplete(
  entry: SessionReportAuthorFeedback,
  report: ReportLegacyAuthorFields,
): boolean {
  const resolved = resolveEntryForCompletion(entry, report);
  return hasRequiredAuthorRating(resolved.rating);
}

/** Slots expected for VSC validation: tutors only (not volunteers/VSC). */
function getTutorSlotsForValidation(
  report: ReportForValidation,
  session?: SessionForReport,
  viewerUserId?: string,
): SessionReportAuthorFeedback[] {
  const participants = session?.participants ?? [];
  return normalizeAuthorFeedbacks(report).filter((entry) => {
    if (viewerUserId && entry.idAuteur === viewerUserId) return false;
    if (!participants.length) return true;
    const p = participants.find((x) => x.userId === entry.idAuteur);
    if (!p) return true;
    return p.role === 'tutor';
  });
}

/** Whole-class report with several expected tutors (tutor slots in `authorFeedbacks`). */
export function isWholeClassMultiTutorReport(
  report: ReportForValidation,
  session?: SessionForReport,
): boolean {
  if (resolveSessionType(report, session) !== 'whole_class') return false;
  return getTutorSlotsForValidation(report, session).length > 1;
}

/** Every tutor has filled their part of the report. */
export function areAllTutorFeedbacksComplete(
  report: ReportForValidation,
  options?: ReportValidationOptions,
): boolean {
  return getIncompleteAuthorFeedbacks(report, options).length === 0;
}

/** Tutor slots without a rating (not treated as written). */
export function getIncompleteAuthorFeedbacks(
  report: ReportForValidation,
  options?: ReportValidationOptions,
): SessionReportAuthorFeedback[] {
  const session = options?.session;
  if (!isWholeClassMultiTutorReport(report, session)) return [];
  return getTutorSlotsForValidation(report, session, options?.viewerUserId).filter(
    (entry) => !isAuthorFeedbackSlotComplete(entry, report),
  );
}

/**
 * VSC warning modal: whole-class session, report submitted,
 * at least one tutor has not completed their part.
 */
export function shouldShowReportValidationModal(
  report: ReportForValidation,
  options?: ReportValidationOptions,
): boolean {
  if (report.status !== 'submitted') return false;
  if (!isWholeClassMultiTutorReport(report, options?.session)) return false;
  return getIncompleteAuthorFeedbacks(report, options).length > 0;
}

/** Catalog line for one or many tutors who have not completed their slot. */
function formatMissingTutorsLine(incompleteCount: number): string {
  if (incompleteCount === 1) return getErrorMessage('reportIncompleteTutorOne');
  return getErrorMessage({ code: 'reportIncompleteTutorsMany', count: incompleteCount });
}

/** Modal copy (confirm / cancel) when not every tutor has written the report. */
export function getIncompleteTutorsModalDescription(
  report: ReportForValidation,
  options?: ReportValidationOptions,
): string {
  const incomplete = getIncompleteAuthorFeedbacks(report, options);
  const missing = formatMissingTutorsLine(incomplete.length);

  return `${missing}\n\n${getErrorMessage('waitForTutorsBeforeValidate')}\n\n${getErrorMessage('confirmValidateReportAnyway')}`;
}

/** Info-banner copy on the report detail view. */
export function getIncompleteTutorsBannerText(
  report: ReportForValidation,
  options?: ReportValidationOptions,
): string | null {
  if (!shouldShowReportValidationModal(report, options)) return null;
  const incomplete = getIncompleteAuthorFeedbacks(report, options);
  return `${formatMissingTutorsLine(incomplete.length)} ${getErrorMessage('waitForTutorsBeforeValidate')}`;
}

/** True when any selected report should open the incomplete-tutors modal. */
export function shouldShowBulkReportValidationModal(
  reports: ReportWithId[],
  optionsByReportId: Record<string, ReportValidationOptions>,
): boolean {
  return reports.some((report) =>
    shouldShowReportValidationModal(report, optionsByReportId[report.id]),
  );
}

/** Catalog line for other selected reports that will also be validated. */
function formatOtherReportsAlsoValidatedLine(otherCount: number): string {
  if (otherCount === 1) return getErrorMessage('otherReportAlsoValidated');
  return getErrorMessage({ code: 'otherReportsAlsoValidatedMany', otherCount });
}

/** Lead sentence for the bulk incomplete-tutors validation modal. */
function formatBulkIncompleteSelectionLead(
  incompleteReports: ReportWithId[],
  optionsByReportId: Record<string, ReportValidationOptions>,
): string {
  if (incompleteReports.length === 1) {
    const incomplete = getIncompleteAuthorFeedbacks(
      incompleteReports[0],
      optionsByReportId[incompleteReports[0].id],
    );
    return formatMissingTutorsLine(incomplete.length);
  }
  return getErrorMessage({
    code: 'bulkIncompleteReportsMany',
    count: incompleteReports.length,
  });
}

/** VSC warning modal for a selection that includes incomplete whole-class reports. */
export function getBulkIncompleteTutorsModalDescription(
  reports: ReportWithId[],
  optionsByReportId: Record<string, ReportValidationOptions>,
): string | null {
  const incompleteReports = reports.filter((report) =>
    shouldShowReportValidationModal(report, optionsByReportId[report.id]),
  );
  if (incompleteReports.length === 0) return null;

  if (incompleteReports.length === 1 && reports.length === 1) {
    return getIncompleteTutorsModalDescription(
      incompleteReports[0],
      optionsByReportId[incompleteReports[0].id],
    );
  }

  const paragraphs = [formatBulkIncompleteSelectionLead(incompleteReports, optionsByReportId)];

  const otherCount = reports.length - incompleteReports.length;
  if (otherCount > 0) {
    paragraphs.push(formatOtherReportsAlsoValidatedLine(otherCount));
  }

  paragraphs.push(
    getErrorMessage('waitForTutorsBeforeValidate'),
    getErrorMessage('confirmValidateSelectionAnyway'),
  );

  return paragraphs.join('\n\n');
}

/** Session type/participants used for VSC validation, from detail or the report. */
function resolveSessionForValidation(
  report: ReportForValidation,
  session?: SessionWithParticipants | null,
): SessionForReport | undefined {
  if (session) {
    return { type: session.type, participants: session.participants };
  }
  if (report.session?.type) {
    return {
      type: report.session.type as SessionType,
      participants: undefined,
    };
  }
  return undefined;
}

/** Session context (participants) for VSC validation — from the detail view or session API. */
export function buildReportValidationOptions(
  report: ReportForValidation,
  session?: SessionWithParticipants | null,
  viewerUserId?: string,
): ReportValidationOptions {
  return {
    session: resolveSessionForValidation(report, session),
    viewerUserId,
  };
}
